/* eslint-disable @typescript-eslint/naming-convention */

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	signal
} from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth, { isAccessToken } from '@okta/okta-auth-js';
import { of, Subject, takeUntil } from 'rxjs';

import { NotificationApiMessageCenterApiService } from '@shure/cloud/shared/notifications/notifications-data-access';
import { NotificationsStore } from '@shure/cloud/shared/notifications/notifications-store';
import { RealTimeInAppBrowserNotificationService } from '@shure/cloud/shared/services/websocket';
import { PrismIconButtonComponent, PrismBadgeComponent } from '@shure/prism-angular-components';

import { NotificationsPopupComponent } from './ui/components/notifications-popup/notifications-popup.component';

@Component({
	selector: 'sh-notifications-bell-icon-main',
	imports: [TranslocoModule, PrismIconButtonComponent, PrismBadgeComponent, NotificationsPopupComponent],
	templateUrl: './notifications-bell-icon-main.component.html',
	styleUrl: './notifications-bell-icon-main.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsBellIconMainComponent implements OnInit, OnDestroy {
	@Input() public application: string | undefined = '';
	@Input() public notificationsWebSocketUrl: string | undefined = '';
	private destroy$: Subject<void> = new Subject<void>();
	public notificationsCount = signal<number>(0);
	public totalNotificationsCount = 0;
	public isNotificationsEnabled = signal(false);
	public nextPageToken?: string;
	constructor(
		@Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
		public cdr: ChangeDetectorRef,
		private notificationService: NotificationApiMessageCenterApiService,
		private notificationsStore: NotificationsStore,
		private realTimeInAppBrowserNotificationService: RealTimeInAppBrowserNotificationService
	) {}

	public async ngOnInit(): Promise<void> {
		this.initializeNotifications();
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Fetches notifications from the server and updates the notification store.
	 */
	public getUnReadNotifications(): void {
		this.notificationService
			.getNotificationStats$Response()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (notificationResponse) => {
					this.notificationsCount.set(notificationResponse.body.unread);
				},
				error: () => of(null)
			});
	}

	/**
	 * Sets up notifications functionality, including subscriptions and WebSocket connection.
	 * Called when notifications are enabled.
	 */
	private initializeNotifications(): void {
		this.notificationsStore.unReadNotificationsCount$
			.pipe(takeUntil(this.destroy$))
			.subscribe((unreadNotificationsCount) => {
				this.notificationsCount.set(unreadNotificationsCount);
				this.cdr.detectChanges();
			});
		this.getUnReadNotifications();
		if (!this.realTimeInAppBrowserNotificationService.isWebSocketConnected()) {
			this.oktaAuth.tokenManager.get('accessToken').then((tokenData) => {
				if (isAccessToken(tokenData) && tokenData?.accessToken) {
					this.realTimeInAppBrowserNotificationService.connectWebSocket({
						application: this.application,
						token: tokenData.accessToken,
						notificationsWebSocketUrl: this.notificationsWebSocketUrl
					});
				}
			});
		}
	}
}
